import classNames from 'classnames';
import type { TabProps, TabVariant } from './Tab.types';

const getColorClassName = (disabled?: boolean, selected?: boolean) => {
  if (disabled) {
    return {
      outer: 'text-status-disabled-dark',
      inner: 'text-status-disabled-dark',
    };
  }
  if (selected) {
    return {
      outer: 'text-status-selected bg-default',
      inner: 'text-status-selected',
    };
  }
  return {
    outer:
      'text-default bg-shade-10 hover:bg-state-hover active:bg-state-press',
    inner: 'text-default bg-none',
  };
};

const getMarkerClassName = (disabled?: boolean, selected?: boolean) => {
  if (disabled) {
    return selected
      ? 'shadow-bottom-border-lg-disabled'
      : 'shadow-bottom-border-disabled';
  }

  return selected
    ? 'shadow-bottom-border-lg-selected'
    : 'shadow-bottom-border-default hover:shadow-bottom-border-lg-default active:shadow-bottom-border-lg-default';
};

const baseVariantClassName: { [Key in TabVariant]: string } = {
  outer: 'rounded-b-none rounded-t-sm',
  inner: 'rounded-none',
};

const buttonVariantClassName: { [Key in TabVariant]: string } = {
  outer: `${baseVariantClassName.outer} py-2xs px-xs gap-3xs`,
  inner: `${baseVariantClassName.inner} py-2xs px-0 gap-3xs`,
};

const focusVariantClassName: { [Key in TabVariant]: string } = {
  outer: 'focus-visible:shadow-tab-ring',
  inner: 'focus-visible:ring',
};

type TabClassNames = {
  root: string;
  button: string;
  label: string;
  icon: string;
  closeButton: string;
};

export const getTabClassNames = <TKey extends string = string>({
  className,
  disabled,
  selected,
  variant = 'outer',
}: TabProps<TKey>): TabClassNames => {
  return {
    root: classNames(
      'flex',
      baseVariantClassName[variant],
      !disabled && variant === 'outer' ? 'bg-default' : 'bg-none',
      className,
    ),
    button: classNames(
      'flex items-center',
      buttonVariantClassName[variant],
      disabled
        ? 'pointer-events-none'
        : `cursor-pointer focus-visible:outline-none ${focusVariantClassName[variant]}`,
      variant === 'inner' && getMarkerClassName(disabled, selected),
      getColorClassName(disabled, selected)[variant],
    ),
    label: 'font-semibold',
    icon: '',
    closeButton: 'text-default hover:text-status-error',
  };
};
